import { TrackingCategory } from '@/entities/tracking/const';
import { TrackingType } from '@/entities/tracking/const/type';
import { TrackerEngine } from '@/entities/tracking/lib/tracker-engine';

import { appLogger } from '../app-logger/appLogger';
import { toError } from '../errors/toError';

/**
 * Clears the accumulated optimizelyExp value from the tracking snapshot.
 *
 * optimizelyExp is intentionally additive *within a single page*: DirectAB
 * (`updateOptimizelyExp`) and the app-base snapshot (`createOptimizelyExpString`)
 * each union their variants onto the existing value so neither writer clobbers
 * the other's contribution for the current page.
 *
 * On SPA navigation, however, nothing reset the value, so variants applied on an
 * earlier page leaked into the optimizelyExp reported on later pages for the life
 * of the `window.dataHub` instance. Call this at the route boundary so each page
 * rebuilds optimizelyExp from an empty baseline, matching a fresh direct load.
 *
 * Both stores need clearing: `window.adobeDataLayer` merges pushes rather than replacing
 * them, so clearing only the TrackerEngine snapshot leaves the previous page's value live.
 */
export async function resetOptimizelyExp(): Promise<void> {
    // Before the await so a racing page view cannot read the previous value; an absent
    // store holds nothing stale, so it is deliberately not created here.
    globalThis.window?.adobeDataLayer?.push({ siteInfo: { optimizelyExp: '' } });

    try {
        await TrackerEngine.update({
            type: TrackingType.INFO_UPDATE,
            detail: {
                siteInfo: {
                    optimizelyExp: '',
                },
            },
            category: TrackingCategory.App.OPERATION,
        });
    } catch (error) {
        appLogger.error(
            'Error resetting optimizelyExp',
            { method: 'resetOptimizelyExp' },
            toError(error),
        );
    }
}
